import type { Location, ShouldRevalidateFunction } from '@remix-run/react';
import { useEffect } from 'react';
import type { CapiProductPage } from 'types/capi/pdp';

import Breadcrumbs from '~/components/BreadCrumbs';
import DownloadAppBlock from '~/components/DownloadAppBlock';
import Error404 from '~/components/Error404';
import HorizontalLine from '~/components/HorizontalLine';
import { Carousel } from '~/components/Pdp/Carousel';
import ProductDetails from '~/components/Pdp/ProductDetails/ProductDetails';
import ProductDetailsTruncated from '~/components/Pdp/ProductDetails/ProductDetailsTruncated';
import ProductMetaData from '~/components/Pdp/ProductMetaData';
import SocialSection from '~/components/Pdp/SocialSection';
import RecommendedArticles from '~/components/RecommendedArticles';
import PdpViewEvent from '~/events/PdpViewEvent';
import useEnv from '~/hooks/useEnv';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import { getPage } from '~/services/capi.server';
import type { RouteLoader } from '~/utils/routeHandlers';
import { createLoader } from '~/utils/routeHandlers';
import seo from '~/utils/seo';

import Banner from './Banner';
import MoreColours from './MoreColours';
import ProductCardCarousels from './ProductCardCarousels/ProductCardCarousels';
import Rating from './Rating';
import RelatedCategories from './RelatedCategories';
import WhereToBuy from './WhereToBuy';

type LoaderData = {
  pageData: CapiProductPage;
};

const routeLoader: RouteLoader = async ({ request }, { session }) => {
  return getPage<CapiProductPage>(request, session);
};

export const loader = createLoader(routeLoader, 'Pdp');

export const meta = ({
  data,
  location
}: {
  data?: LoaderData;
  location: Location;
}) => {
  const meta = data
    ? seo.formatForMeta(data.pageData.canonical_url, data.pageData.seo)
    : [];

  if (location.search.includes('variant=')) return seo.addMetaNoIndex(meta);
  if (location.search.includes('raffle=')) return seo.addMetaNoIndex(meta);

  return meta;
};

export default function ProductPage() {
  const env = useEnv();
  const user = useUser();

  const product = usePageData<CapiProductPage>();
  const { clientIp, isBot } = useRootData();

  useStructuredData(product._schemas);

  useEffect(() => {
    new PdpViewEvent({
      clientIp,
      isBot,
      product,
      user
    }).send();
  }, [product, env, clientIp, user, isBot]);

  return (
    <div key={product.product_id}>
      <Banner />
      <div className="mx-auto max-w-6xl px-4 pt-4 md:px-8">
        <div className="hidden pb-8 pt-4 md:flex">
          <Breadcrumbs breadcrumbs={product.breadcrumbs} />
        </div>

        <div className="flex flex-col gap-8">
          <div className="grid md:grid-cols-6 lg:grid-cols-12 lg:gap-6">
            <div className="md:col-span-6 lg:col-span-8 xl:col-span-8">
              <Carousel />
              <div className="my-4 md:mt-10">
                <ProductMetaData />
              </div>
              {product.rating?.count && product.rating?.count > 0 && (
                <div className="mb-4">
                  <Rating />
                </div>
              )}
              <div className="mt-2">
                <MoreColours />
              </div>
              <div className="lg:hidden">
                <ProductDetailsTruncated />
              </div>
              <div className="hidden lg:block">
                <ProductDetails />
              </div>
            </div>
            <WhereToBuy />
          </div>
          <SocialSection />
          <RelatedCategories />
          <HorizontalLine />
        </div>
      </div>
      <div className="pt-12">
        <ProductCardCarousels />
        <DownloadAppBlock />
        {product.recent_articles.length > 0 && (
          <>
            <hr className="border-b-1 w-full border-grey-midnight-read"></hr>
            <div className="bg-concrete">
              <RecommendedArticles
                articles={product.recent_articles}
                viewMoreNewsPath={product.view_more_news_path}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt')) return false;

  return defaultShouldRevalidate;
};

export function ErrorBoundary() {
  return <Error404 />;
}
